import React, { useState, useEffect, useCallback, useRef, useContext, Suspense, lazy } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { AsyncButton } from '../ui/async-button';
import { useAsyncAction } from '../../hooks/useAsyncAction';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Textarea } from '../ui/textarea';
import {
  FormBody,
  FormField,
  FormFooter,
  FormHint,
  FormLabel,
} from '../ui/form-layout';
import { useToast } from '../../hooks/use-toast';
import { apiGet, apiPost, apiDelete } from '../../utils/api';
import { hasAdminLikeAccess } from '../../utils/userAccess';
import { Send } from 'lucide-react';
import PageLoader from '../ui/PageLoader';

// Personal timesheet UI is one shared module: Employee → /employee; PM, RM, DH → /pm/timesheet.
// Missing imports or runtime errors in TimesheetEntry break all four roles (no per-role forks).
const TimesheetEntry = lazy(() => import('../employee/TimesheetEntry'));
import { FEATURE_AI_SURVEY_UI_ENABLED } from '../../config/features';
// Removed mockCurrentUser import

// --- NEW: Import AuthContext ---
import { AuthContext } from '../../context/AuthContext';
import { isAuthRedirectInProgress } from '../../utils/authRedirect';
// --- END NEW IMPORT ---

// --- Sidebar navigation ---
import RoleSidebar, { SIDEBAR_ACTIONS } from './RoleSidebar';
import LayoutHeader from './LayoutHeader';
import LayoutFooter from './LayoutFooter';
import { getFooterMeta } from '../../utils/footerMeta';
import MonthSelect from './MonthSelect';
import { useSidebarState } from '../../hooks/useSidebarState';
import { useFeatureFlags } from '../../context/FeatureFlagsContext';
import WalkthroughBootstrap from '../../features/walkthrough/components/WalkthroughBootstrap';
import WalkthroughOverlay from '../../features/walkthrough/components/WalkthroughOverlay';
import { requestWalkthroughReplay } from '../../features/walkthrough/walkthroughOrchestrator';
import { useWalkthrough } from '../../features/walkthrough/walkthroughContext';
import { NavUiProvider } from './navUiContext';
import { getLayoutScrollMode } from './layoutScrollMode';
import { cn } from '../../lib/utils';
// --- END Sidebar ---

const EmployeeLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Get current year and month dynamically
  const getCurrentYear = () => getFooterMeta().year;

  const getCurrentMonthYear = () => getFooterMeta().monthYear;
  
  // State to track current date for automatic updates
  const [currentDate, setCurrentDate] = useState({
    year: getCurrentYear(),
    monthYear: getCurrentMonthYear()
  });
  
  // Update date periodically (every minute) to catch month/year changes
  useEffect(() => {
    const updateDate = () => {
      setCurrentDate({
        year: getCurrentYear(),
        monthYear: getCurrentMonthYear()
      });
    };
    
    // Update immediately
    updateDate();
    
    // Update every minute to catch month/year changes
    const interval = setInterval(updateDate, 60000);
    
    return () => clearInterval(interval);
  }, []);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [availableMonths, setAvailableMonths] = useState([]);
  const [projectRequestOpen, setProjectRequestOpen] = useState(false);
  const [requestDescription, setRequestDescription] = useState('');
  const [faqsOpen, setFaqsOpen] = useState(false);
  const [totalMonthHours, setTotalMonthHours] = useState(0);
  const [canSubmit, setCanSubmit] = useState(false);
  const [timesheetStatus, setTimesheetStatus] = useState('draft');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const handleSubmitTimesheetCallback = useRef(null);
  const handleSaveTimesheetCallback = useRef(null);
  const [notifications, setNotifications] = useState([]);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const sidebar = useSidebarState();

  // Project Access Request notification (Admin only)
  // Initialize as null - will only be set for Admin users
  const [pendingRequestsCount, setPendingRequestsCount] = useState(null);
  const [deletingNotificationId, setDeletingNotificationId] = useState(null);

  // --- NEW: Get user and logout from context ---
  const { user, logout, isLoading } = useContext(AuthContext);
  const rmMenuAccess = !!user?.isReportingManager;
  const { isWalkthroughEnabled } = useFeatureFlags();
  const { startTour, endActiveTour } = useWalkthrough();

  const handleAppTour = useCallback(() => {
    if (!isWalkthroughEnabled || !user) return;
    void requestWalkthroughReplay({
      user,
      navigate,
      startTour,
      endActiveTour,
      isMobile: sidebar.mode === 'mobile',
      openMobileNav: sidebar.openMobileDrawer,
    });
  }, [endActiveTour, isWalkthroughEnabled, navigate, sidebar.mode, sidebar.openMobileDrawer, startTour, user]);

  useEffect(() => {
    // Generate available months starting from employee's joining date
    if (!user || isLoading) {
      // Wait for user to load before generating months
      return;
    }

    const months = [];
    const now = new Date();
    
    // Get employee's joining date (createdAt)
    let joiningDate = null;
    if (user.createdAt) {
      joiningDate = new Date(user.createdAt);
      // Set to first day of the joining month
      joiningDate.setDate(1);
      joiningDate.setHours(0, 0, 0, 0);
    }
    
    // If no joining date, default to 13 months back (fallback)
    const startDate = joiningDate || new Date(now.getFullYear(), now.getMonth() - 12, 1);
    
    // Generate months from joining date (or startDate) to current month
    // Start from current month and go backwards to joining date (newest first)
    const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    let currentDate = new Date(currentMonth);
    
    // Generate months going backwards from current month until we reach joining date
    // This creates an array with newest month first (matching original behavior)
    while (currentDate >= startDate) {
      const monthKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
      const monthLabel = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      months.push({ key: monthKey, label: monthLabel });
      
      // Move to previous month
      currentDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
    }
    
    setAvailableMonths(months);
    // Set selected month to current month (first in the list)
    if (months.length > 0) {
      setSelectedMonth(months[0].key);
    }
  }, [user, isLoading]);

  const { execute: executeSignOut, isPending: isSigningOut } = useAsyncAction(async () => {
    console.log('Signing out...');
    await logout();
  });

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!user || isLoading || isAuthRedirectInProgress()) return;
    
    try {
      const result = await apiGet('/api/notifications');
      if (result.success) {
        setNotifications(result.data.notifications || []);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, [user, isLoading]);

  // Check and generate notifications on login
  const checkAndGenerateNotifications = useCallback(async () => {
    if (!user || isLoading || isAuthRedirectInProgress()) return;
    
    try {
      await apiPost('/api/notifications/check');
      // Refresh notifications after generating
      fetchNotifications();
    } catch (error) {
      console.error('Error checking notifications:', error);
    }
  }, [user, isLoading, fetchNotifications]);

  const handleDeleteNotification = async (notificationId, e) => {
    e.stopPropagation();

    setDeletingNotificationId(notificationId);
    try {
      const result = await apiDelete(`/api/notifications/${notificationId}`);
      if (result.success) {
        setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
        toast({
          title: 'Notification deleted',
          variant: 'default',
        });
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete notification',
        variant: 'destructive',
      });
    } finally {
      setDeletingNotificationId(null);
    }
  };

  // Fetch pending project access requests count (Admin only)
  // This function should NEVER be called for non-admin users
  const fetchPendingRequestsCount = useCallback(async () => {
    // Strict check: only proceed if user is Admin
    if (!user || isLoading || !hasAdminLikeAccess(user)) {
      // For non-admin users, ensure state remains null
      // This should never be called for non-admin users, but adding safety check
      setPendingRequestsCount(null);
      return;
    }
    
    // Only fetch for Admin users
    try {
      const result = await apiGet('/api/project-access-requests?status=pending&page=1&limit=1');
      if (result.success) {
        const n = Number(result.totalRecords ?? result.pagination?.total ?? result.data?.length ?? 0);
        setPendingRequestsCount(n);
      } else {
        setPendingRequestsCount(0);
      }
    } catch (error) {
      console.error('Error fetching pending requests count:', error);
      // Don't show error toast for background polling
      setPendingRequestsCount(0);
    }
  }, [user, isLoading]);

  // Fetch notifications when user loads
  useEffect(() => {
    if (user && !isLoading) {
      checkAndGenerateNotifications();
      // Refresh notifications every 30 seconds
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user, isLoading, checkAndGenerateNotifications, fetchNotifications]);

  // Fetch pending requests count for Admin (poll every 30 seconds)
  // CRITICAL: This effect must ensure state is null for non-admin users
  useEffect(() => {
    // If user is not loaded or still loading, set to null
    if (!user || isLoading) {
      setPendingRequestsCount(null);
      return;
    }
    
    // Only proceed if user is Admin
    if (hasAdminLikeAccess(user)) {
      // Fetch immediately and set up polling for Admin users only
      fetchPendingRequestsCount();
      const interval = setInterval(fetchPendingRequestsCount, 30000);
      return () => {
        clearInterval(interval);
        // Cleanup: reset to null when component unmounts or user changes
        setPendingRequestsCount(null);
      };
    } else {
      // CRITICAL: For non-admin users, explicitly set to null
      // This ensures no rendering happens for Employee/Project Manager
      setPendingRequestsCount(null);
    }
  }, [user, isLoading, fetchPendingRequestsCount]);

  const submitProjectRequest = async () => {
    if (!requestDescription.trim()) {
      toast({ title: "Description Required", description: "Please provide a description...", variant: "destructive" });
      return;
    }
    
    if (requestDescription.trim().length > 500) {
      toast({ title: "Character Limit Exceeded", description: "Description must be 500 characters or less.", variant: "destructive" });
      return;
    }
    
    try {
      const { apiPost } = await import('../../utils/api');
      const result = await apiPost('/api/project-access-requests', {
        requestMessage: requestDescription.trim()
      });
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to submit request');
      }
      
      toast({ title: "Project Request Sent", description: "Request sent to administrator.", variant: "success" });
      setRequestDescription('');
      setProjectRequestOpen(false);
    } catch (error) {
      console.error('Error submitting project request:', error);
      toast({ 
        title: "Error", 
        description: error.message || "Failed to submit request. Please try again.", 
        variant: "destructive" 
      });
    }
  };

  const { execute: handleProjectRequest, isPending: isSubmittingRequest } =
    useAsyncAction(submitProjectRequest);

  const registerSubmitHandler = useCallback((submitFunction) => { /* ... (no changes needed) ... */
    handleSubmitTimesheetCallback.current = submitFunction;
    console.log("Submit handler registered in Layout:", !!submitFunction);
  }, []);

  const registerSaveHandler = useCallback((saveFunction) => {
    handleSaveTimesheetCallback.current = saveFunction;
    console.log("Save handler registered in Layout:", !!saveFunction);
  }, []);

  const handleSaveTimesheet = () => {
    if (handleSaveTimesheetCallback.current) {
      console.log("Calling save handler from Layout...");
      handleSaveTimesheetCallback.current();
    } else {
      console.error("Save handler not registered from TimesheetEntry component!");
      toast({ title: "Error", description: "Save function not available.", variant: "destructive"});
    }
  };

  // Force refresh status after submission



  const handleSubmitTimesheet = () => { /* ... (no changes needed) ... */
    if (!canSubmit) {
      toast({ title: "Cannot Submit", description: "Please add at least one timesheet entry before submitting.", variant: "destructive" });
      return;
    }
    if (handleSubmitTimesheetCallback.current) {
        console.log("Calling submit handler from Layout...");
        handleSubmitTimesheetCallback.current();
    } else {
        console.error("Submit handler not registered from TimesheetEntry component!");
        toast({ title: "Error", description: "Submit function not available.", variant: "destructive"});
    }
  };


  const isCurrentMonth = () => { /* ... (no changes needed) ... */
    const now = new Date();
    const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    return selectedMonth === currentMonthKey;
  };

  if (!user && isAuthRedirectInProgress()) {
    return null;
  }

  if (isLoading || !user) {
    return (
      <div className="layout-shell flex w-full items-center justify-center bg-gray-50 dark:bg-gray-900">
        <PageLoader />
      </div>
    );
  }

  const isPersonalTimesheetView =
    location.pathname === '/employee' || location.pathname === '/pm/timesheet';
  const showMonthSelector =
    isPersonalTimesheetView &&
    (!hasAdminLikeAccess(user) || location.pathname === '/pm/timesheet');
  const showHoursDisplay =
    isPersonalTimesheetView &&
    (location.pathname === '/pm/timesheet' ||
      (!hasAdminLikeAccess(user) && location.pathname === '/employee'));
  const showSubmitButton = showHoursDisplay;

  const handleSidebarAction = (actionId) => {
    if (actionId === SIDEBAR_ACTIONS.REQUEST_PROJECT_ACCESS) {
      setProjectRequestOpen(true);
      return;
    }
    if (actionId === SIDEBAR_ACTIONS.FAQS) {
      setFaqsOpen(true);
      return;
    }
    if (actionId === SIDEBAR_ACTIONS.SIGN_OUT) {
      executeSignOut();
    }
  };

  const sidebarDrawerFooter = (
    <>
      {showMonthSelector && (
        <div data-walkthrough="timesheet-month-nav-mobile">
          <label htmlFor="sidebar-month-select" className="filter-label mb-2 block">
            View Month
          </label>
          <MonthSelect
            id="sidebar-month-select"
            value={selectedMonth}
            onValueChange={setSelectedMonth}
            months={availableMonths}
            touchFriendly
          />
        </div>
      )}
      {showHoursDisplay && (
        <div
          className="flex items-center justify-between px-3 py-2.5 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800"
          data-walkthrough="timesheet-hours-summary-mobile"
        >
          <span className="text-ds-caption text-blue-700 dark:text-blue-300 font-medium">Total Hours</span>
          <span className="text-ds-h3 text-blue-900 dark:text-blue-200">{totalMonthHours}h</span>
        </div>
      )}
      {showSubmitButton && timesheetStatus === 'draft' && (
        <Button
          onClick={() => {
            handleSubmitTimesheet();
            sidebar.closeMobileDrawer();
          }}
          loading={isSubmitting}
          disabled={!canSubmit || isSaving}
          data-walkthrough="timesheet-submit-mobile"
          className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 h-11"
        >
          {isSubmitting ? (
            'Submitting...'
          ) : (
            <>
              <Send className="h-4 w-4 mr-2" />
              Submit for Approval
            </>
          )}
        </Button>
      )}
    </>
  );


  const isSurveyFullBleed =
    FEATURE_AI_SURVEY_UI_ENABLED &&
    (location.pathname === '/employee/ai-agent-survey' ||
      location.pathname.startsWith('/admin/survey-response/'));

  const layoutScrollMode = children
    ? getLayoutScrollMode(location.pathname, {
        isMobile: sidebar.mode === 'mobile',
        isTablet: sidebar.mode === 'tablet',
      })
    : 'embed';
  const isMainScrollable = layoutScrollMode === 'content';
  const mainPadding = isSurveyFullBleed ? 'p-0' : 'layout-main-padding';

  const timesheetEntryProps = {
    selectedMonth,
    availableMonths,
    onMonthChange: setSelectedMonth,
    onHoursChange: setTotalMonthHours,
    onCanSubmitChange: setCanSubmit,
    onSubmitTimesheet: registerSubmitHandler,
    onSaveTimesheet: registerSaveHandler,
    onStatusChange: setTimesheetStatus,
    onSubmitLoadingChange: setIsSubmitting,
    onSaveLoadingChange: setIsSaving,
  };

  const layoutHeaderProps = {
    user,
    rmMenuAccess,
    sidebar,
    onAppTour: isWalkthroughEnabled ? handleAppTour : undefined,
    showMonthSelector,
    showHoursDisplay,
    showSubmitButton,
    selectedMonth,
    onMonthChange: setSelectedMonth,
    availableMonths,
    totalMonthHours,
    timesheetStatus,
    canSubmit,
    isSubmitting,
    isSaving,
    onSubmit: handleSubmitTimesheet,
    notifications,
    notificationOpen,
    onNotificationOpenChange: setNotificationOpen,
    onDeleteNotification: handleDeleteNotification,
    deletingNotificationId,
    pendingRequestsCount,
  };

  const timesheetEntryNode = (
    <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden">
      <Suspense fallback={<PageLoader />}>
        <TimesheetEntry {...timesheetEntryProps} />
      </Suspense>
    </div>
  );

  return (
    <NavUiProvider
      openMobileNav={sidebar.openMobileDrawer}
      expandSidebar={sidebar.expandSidebar}
      isMobile={sidebar.mode === 'mobile'}
      isSidebarCollapsed={sidebar.isCollapsed}
    >
    <div className="layout-shell overflow-hidden bg-gray-50 dark:bg-gray-900 flex flex-col print:overflow-visible">
      {sidebar.mode === 'mobile' ? (
        <div className="print:hidden">
        <LayoutHeader
          {...layoutHeaderProps}
          layout="full"
        />
        </div>
      ) : null}

      <Dialog open={projectRequestOpen} onOpenChange={setProjectRequestOpen}>
        <DialogContent className="sm:max-w-md dark:bg-gray-800 dark:border-gray-700">
          <DialogHeader>
            <DialogTitle className="dark:text-gray-100">Request Missing Project</DialogTitle>
            <DialogDescription className="dark:text-gray-400">
              Describe the project and client you need access to. An admin will review your request.
            </DialogDescription>
          </DialogHeader>
          <FormBody>
            <FormField>
              <FormLabel htmlFor="project-request-description">
                Describe the project you need access to
              </FormLabel>
              <div className="relative">
                <Textarea
                  id="project-request-description"
                  value={requestDescription}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) {
                      setRequestDescription(e.target.value);
                    }
                  }}
                  placeholder="Project name, client details, and any other relevant information..."
                  className="pr-16 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                  rows={3}
                  maxLength={500}
                />
                <div className="pointer-events-none absolute bottom-2 right-2">
                  <FormHint
                    className={
                      requestDescription.length > 450
                        ? 'text-orange-500 dark:text-orange-400'
                        : requestDescription.length >= 500
                          ? 'text-destructive'
                          : undefined
                    }
                  >
                    {requestDescription.length}/500
                  </FormHint>
                </div>
              </div>
            </FormField>
            <FormFooter>
              <Button
                variant="outline"
                onClick={() => setProjectRequestOpen(false)}
                disabled={isSubmittingRequest}
                className="h-11 w-full dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-700 md:h-8 sm:w-auto"
              >
                Cancel
              </Button>
              <AsyncButton
                onClick={handleProjectRequest}
                pending={isSubmittingRequest}
                loadingText="Sending…"
                className="h-11 w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 md:h-8 sm:w-auto"
              >
                Send Request
              </AsyncButton>
            </FormFooter>
          </FormBody>
        </DialogContent>
      </Dialog>

      <Dialog open={faqsOpen} onOpenChange={setFaqsOpen}>
        <DialogContent className="md:max-w-4xl max-h-[90dvh] overflow-hidden flex flex-col dark:bg-gray-800 dark:border-gray-700">
          <DialogHeader>
            <DialogTitle className="dark:text-gray-100">Worklog Management – FAQs</DialogTitle>
            <DialogDescription className="dark:text-gray-400">
              Common questions about filling timesheets, leave, holidays, and submitting for approval.
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 pr-2 faq-scrollbar">
            <Accordion type="single" collapsible className="w-full faq-accordion">
                      <AccordionItem value="item-3" className="border-b border-gray-200 dark:border-gray-700">
                          <AccordionTrigger className="text-left font-medium text-gray-900 dark:text-gray-100 hover:no-underline text-ds-body">
                            What if I don't see my client / project name in the list?
                          </AccordionTrigger>
                          <AccordionContent className="text-gray-700 dark:text-gray-300 leading-relaxed">
                            If your client or project is not listed in the timesheet dropdown, open the navigation sidebar and select "Request Project Access", then enter the required project and client details in the form provided.
                            
                            Once submitted, your request will be sent to the Admin for review and approval. After approval, the project will appear in your timesheet for selection.
                          </AccordionContent>
                        </AccordionItem>
                        
                        <AccordionItem value="item-1" className="border-b border-gray-200 dark:border-gray-700">
                          <AccordionTrigger className="text-left font-medium text-gray-900 dark:text-gray-100 hover:no-underline text-ds-body">
                            How do I fill the timesheet?
                          </AccordionTrigger>
                          <AccordionContent className="text-gray-700 dark:text-gray-300 leading-relaxed">
                            After signing in, you will be redirected to the Employee Timesheet page. For each working day, select the appropriate option from the dropdown available near the date. Then, search and select the client you are working for, followed by choosing the relevant project name.
                            
                            Next, enter the number of hours worked for that day and provide a clear, concise, and accurate task description explaining the work you performed. Ensure that the task description reflects the actual activities completed during the day.
                            
                            Once all the required details are entered and verified, click Save to record the timesheet entry.
                          </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="item-2" className="border-b border-gray-200 dark:border-gray-700">
                          <AccordionTrigger className="text-left font-medium text-gray-900 dark:text-gray-100 hover:no-underline text-ds-body">
                            Can I log hours for multiple projects on the same day?
                          </AccordionTrigger>
                          <AccordionContent className="text-gray-700 dark:text-gray-300 leading-relaxed">
                            Yes, you can log hours for multiple projects on a single day. To do this, use the (+) button next to the existing entry to add a new project row for that day.
                            
                            For each additional project, you can enter the project name, worked hours, and a clear task description. If you need to remove a project entry, simply click the (−) button next to the corresponding entry.
                          </AccordionContent>
                        </AccordionItem>

                        

                        <AccordionItem value="item-4" className="border-b border-gray-200 dark:border-gray-700">
                          <AccordionTrigger className="text-left font-medium text-gray-900 dark:text-gray-100 hover:no-underline text-ds-body">
                            What should I do if I want to take leave for a day?
                          </AccordionTrigger>
                          <AccordionContent className="text-gray-700 dark:text-gray-300 leading-relaxed">
                            If you wish to apply for leave for a specific day, select the Leave option from the working day dropdown available for that date. Once selected, the system will mark the day as a leave.
                            
                            The leave will be automatically considered in your timesheet, and 8 hours will be added to your total working hours in accordance with the company's leave policy.
                          </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="item-5" className="border-b border-gray-200 dark:border-gray-700">
                          <AccordionTrigger className="text-left font-medium text-gray-900 dark:text-gray-100 hover:no-underline text-ds-body">
                            What should I do if a day is declared as a holiday?
                          </AccordionTrigger>
                          <AccordionContent className="text-gray-700 dark:text-gray-300 leading-relaxed">
                            If a particular day is declared as a holiday, select the Holiday option from the working day dropdown for that date. Once selected, the system will mark the day as a holiday.
                            
                            The holiday will be automatically accounted for in your timesheet, and 8 hours will be added to your total working hours as per the company policy.
                          </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="item-6" className="border-b border-gray-200 dark:border-gray-700">
                          <AccordionTrigger className="text-left font-medium text-gray-900 dark:text-gray-100 hover:no-underline text-ds-body">
                            How do I log a half-day leave?
                          </AccordionTrigger>
                          <AccordionContent className="text-gray-700 dark:text-gray-300 leading-relaxed">
                            If you took a half-day leave, the system will automatically add 4 hours to your total working hours. You can also enter the actual hours you worked that day, and both will be counted. For regular working days, only the hours you enter will be counted.
                          </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="item-7" className="border-b border-gray-200 dark:border-gray-700">
                          <AccordionTrigger className="text-left font-medium text-gray-900 dark:text-gray-100 hover:no-underline text-ds-body">
                            What should I do if I want to take leave at the end of the month?
                          </AccordionTrigger>
                          <AccordionContent className="text-gray-700 dark:text-gray-300 leading-relaxed">
                            If you plan to take leave at the end of the month, ensure that you complete and fill in the timesheet for all working days prior to your leave. For the days you intend to take leave, select the Leave option from the working day dropdown for each applicable date.
                           
                            After marking the leave days and verifying all entries, submit the timesheet for approval. This ensures that your leave is properly recorded and the timesheet is processed without delays.
                          </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="item-8" className="border-b border-gray-200 dark:border-gray-700">
                          <AccordionTrigger className="text-left font-medium text-gray-900 dark:text-gray-100 hover:no-underline text-ds-body">
                            Can I fill timesheet entries on weekends?
                          </AccordionTrigger>
                          <AccordionContent className="text-gray-700 dark:text-gray-300 leading-relaxed">
                            No, timesheet entries for weekends are currently disabled in this version, as weekends are considered company off days.
                            
                            In future versions, the system may allow logging hours on weekends if required.
                          </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="item-9" className="border-b border-gray-200 dark:border-gray-700">
                          <AccordionTrigger className="text-left font-medium text-gray-900 dark:text-gray-100 hover:no-underline text-ds-body">
                            How can I save a partially completed timesheet?
                          </AccordionTrigger>
                          <AccordionContent className="text-gray-700 dark:text-gray-300 leading-relaxed">
                            If you have filled in the timesheet for a partial working period, click the Save button available at the top-right corner of the page. This action will save your timesheet as a draft.
                            
                            A saved draft can be edited, updated, and re-saved at any time before final submission, allowing you to complete your timesheet gradually as needed.
                          </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="item-10" className="border-b border-gray-200 dark:border-gray-700">
                          <AccordionTrigger className="text-left font-medium text-gray-900 dark:text-gray-100 hover:no-underline text-ds-body">
                            What does the "Submit for Approval" button do?
                          </AccordionTrigger>
                          <AccordionContent className="text-gray-700 dark:text-gray-300 leading-relaxed">
                            The Submit for Approval button allows you to send your completed timesheet to your Project Manager for review and approval.
                            
                            You can submit your timesheet only after completing a minimum of 160 total working hours for the month. Once submitted, the timesheet will be locked for editing, and the manager will review and approve the entries.
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>

                      <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                        <h3 className="text-ds-h2 text-gray-900 dark:text-gray-100 mb-4">Do's & Don'ts (Important Guidelines)</h3>
                        <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                          <li className="flex items-start">
                            <span className="text-gray-600 dark:text-gray-400 mr-2 font-semibold">•</span>
                            <span>For a full working day, enter at least 8 work hours (maximum 12).</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-gray-600 dark:text-gray-400 mr-2 font-semibold">•</span>
                            <span>For a half-day leave, enter at least 4 work hours (maximum 6).</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-gray-600 dark:text-gray-400 mr-2 font-semibold">•</span>
                            <span>Timesheets can be submitted for approval only after the total working hours reach a minimum of 160 hours for the month.</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-gray-600 dark:text-gray-400 mr-2 font-semibold">•</span>
                            <span>Only one row per project per day — use separate rows for different projects.</span>
                          </li>
                        </ul>
                      </div>
          </div>
        </DialogContent>
      </Dialog>

      <div
        className={cn(
          'flex min-w-0 flex-1 flex-row min-h-0 overflow-hidden bg-gray-50 dark:bg-gray-900 print:overflow-visible',
          sidebar.mode === 'mobile'
            ? 'pt-[var(--layout-header-offset)]'
            : 'pt-[var(--browser-recommendation-banner-h,0px)]'
        )}
      >
        <div className="print:hidden">
        <RoleSidebar
          user={user}
          mode={sidebar.mode}
          collapsed={sidebar.isCollapsed}
          mobileDrawerOpen={sidebar.mobileDrawerOpen}
          onToggleCollapsed={sidebar.toggleCollapsed}
          onCloseMobileDrawer={sidebar.closeMobileDrawer}
          onAction={handleSidebarAction}
          pendingActionId={isSigningOut ? SIDEBAR_ACTIONS.SIGN_OUT : null}
          drawerFooter={sidebarDrawerFooter}
        />
        </div>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden print:overflow-visible">
        {sidebar.mode !== 'mobile' ? (
          <div className="print:hidden">
          <LayoutHeader
            {...layoutHeaderProps}
            layout="inset"
          />
          </div>
        ) : null}
        <main
          id="main-content"
          tabIndex={-1}
          data-walkthrough="welcome-anchor"
          className={`main-layout-scrollbar flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden dark:bg-gray-900 print:overflow-visible print:h-auto ${
            children
              ? isMainScrollable
                ? `overflow-y-auto ${mainPadding}`
                : `overflow-hidden ${mainPadding}`
              : isPersonalTimesheetView
                ? 'overflow-hidden layout-main-padding'
                : 'overflow-hidden layout-main-padding'
          }`}
        >
          {children ? (
            <div
              className={cn(
                'w-full min-w-0',
                isMainScrollable
                  ? 'layout-main-scroll-child'
                  : 'flex min-h-0 flex-1 flex-col overflow-hidden overflow-x-hidden'
              )}
            >
              {children}
            </div>
          ) : (
            // Show TimesheetEntry for all users on /pm/timesheet route (employee context)
            // This ensures admins with PM role or admins-only can access their own timesheet
            location.pathname === '/pm/timesheet' || !hasAdminLikeAccess(user) ? (
              timesheetEntryNode
            ) : (
              // For admins on other routes, show welcome message
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <p className="text-ds-h2 text-gray-900 mb-2">Welcome, {user.name}!</p>
                  <p className="text-ds-body text-gray-600">Use the sidebar to navigate and get started.</p>
                </div>
              </div>
            )
          )}
        </main>

        <div className="print:hidden">
        <LayoutFooter
          year={currentDate.year}
          monthYear={currentDate.monthYear}
          onAppTour={isWalkthroughEnabled ? handleAppTour : undefined}
        />
        </div>
        </div>
      </div>

      {isWalkthroughEnabled ? (
        <>
          <WalkthroughBootstrap />
          <WalkthroughOverlay />
        </>
      ) : null}
    </div>
    </NavUiProvider>
  );
};

export default EmployeeLayout;


